import history from 'utils/history';

/**
 *
 * SignIn
 *
 */
import PropTypes from 'prop-types';

import React from 'react';
import { connect } from 'react-redux';
import 'react-toastify/dist/ReactToastify.css';

import { createStructuredSelector } from 'reselect';

import PasswordInput from 'atoms/PasswordInput';
import ForgotPass from 'components/ForgotPass';
import { setSignupTrack } from 'containers/App/actions';
import {
	makeSelectAuth,
	makeSelectIsMobile,
	makeSelectLocation,
} from 'containers/App/selectors';
import BrowseGroupsPrivate from 'containers/BrowseGroupsPrivate';
import loadIcon from 'images/load-icon.gif';
import logoLogin from 'images/logo_login.svg';
import { resolveSignupTrack } from 'utils/registrationTracks';

import { forgotPassword, loginUser, toggleForgotPass } from './actions';
import './index.css';
import {
	makeSelectError,
	makeSelectForgotPassToggle,
	makeSelectForgotPassword,
	makeSelectSignIn,
} from './selectors';

/* eslint-disable react/prefer-stateless-function */
export class SignIn extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			error: '',
			showLoading: false,
			password: '',
			email: '',
		};
	}

	redirectToDashboardIfAlreadySignedIn = () => {
		if (!this.props.auth?.isLoggedIn) return;
		const roleName = this.props.auth.loggedInUser?.roleName?.toLowerCase();
		if (!roleName) return;
		const dashboardPath = roleName === 'referrer' ? '/referrer/dashboard' : `/${roleName}`;
		history.push(dashboardPath);
	};

	componentDidMount() {
		this.captureTrackFromQuery();
		this.redirectToDashboardIfAlreadySignedIn();
	}

	// True on the sign-in routes a patient can land on: `/patient/signin` and
	// the generic `/signin` (SignUp's moveToSignIn targets the latter with
	// `?track=<slug>`). Lowercased because react-router v5 routes are
	// case-insensitive.
	isPatientSigninRoute = () => {
		const segments = (this.props.location?.pathname || '')
			.toLowerCase()
			.split('/');
		return (
			segments[1] === 'signin' ||
			(segments[1] === 'patient' && segments[2] === 'signin')
		);
	};

	// A track-typed sign-in link (`?track=<slug>`) carries the registration
	// track through login in client state, so PatientRegister can pick it up
	// after the post-login redirect (which drops the query string). Scoped to
	// patient-capable sign-in routes: `/clinician/signin?track=iop` must not
	// preset a patient registration track.
	//
	// CONSTRAINT — only ever set, never clear. Do NOT add an `else` that clears
	// the track: a brand-new patient reaches /patient/signin with NO ?track (the
	// post-signup redirect drops it) and the track already set by SignUp. Clearing
	// here would wipe it before PatientRegister mounts, breaking the new-account
	// path, and nothing downstream would restore it.
	captureTrackFromQuery = () => {
		if (!this.isPatientSigninRoute()) return;
		const params = new URLSearchParams(this.props.location?.search || '');
		const info = resolveSignupTrack(params.get('track'));
		if (info) {
			this.props.setTrack(info.type);
		}
	};

	componentDidUpdate(prevProps) {
		if (!prevProps.auth?.isLoggedIn && this.props.auth?.isLoggedIn) {
			this.redirectToDashboardIfAlreadySignedIn();
		}
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		const { error } = nextProps;

		if (this.state.error === '' || this.state.error === null) {
			this.setState({
				error,
				showLoading: false,
			});
		}
	}

	signIn = (e) => {
		e.preventDefault();
		this.setState({
			showLoading: true,
		});
		this.props.loginUser({
			email: this.state.email,
			password: this.state.password,
		});
	};

	moveToSignUp = () => {
		const { location } = this.props;

		const path = location.pathname.split('/');
		const role = path[1] === 'signin' ? 'patient' : path[1];

		// Carry a captured ?track into the track-typed signup URL (mirroring
		// moveToSignIn): pushing the plain /patient/signup would clear the
		// track on SignUp's mount (the plain-URL clear is spec'd behavior).
		if ((role || '').toLowerCase() === 'patient') {
			const params = new URLSearchParams(location?.search || '');
			const slug = params.get('track');
			if (resolveSignupTrack(slug)) {
				history.push(`/patient/signup/${slug.toLowerCase()}`);
				return;
			}
		}

		history.push(`/${role}/signup`);
	};

	handleChange(type, event) {
		this.setState({
			[type]: event.target.value,
		});

		this.setState({ error: '' });
	}

	render() {
		const { showLoading } = this.state;
		const { isMobile } = this.props;
		const params = new URLSearchParams(this.props.location?.search || '');
		const verified = params.get('verified') === 'true';

		return (
			<div className="container-fluid p-0 d-flex login bg-white">
				{!isMobile && (
					<div className="p-0 image-login-container container-fluid">
						<div className="row spacer"></div>
						<div className="row">
							<div className="col py-lg-5 d-flex justify-content-center">
								<BrowseGroupsPrivate />
							</div>
						</div>
					</div>
				)}
				<div
					className={`${isMobile ? 'col' : 'col-6'} align-self-center px-xl-5`}
				>
					<div className="d-flex justify-content-center">
						<img className="mb-4" src={logoLogin} alt="logoLogin" />
					</div>
					{verified && (
						<div className="d-flex justify-content-center my-3">
							<div className="alert alert-success font-13 text-center mb-0" role="alert">
								Email verified! Your account is pending approval. You will receive an email once approved.
							</div>
						</div>
					)}
					{this.state.error && (
						<div>
							<div className="d-flex justify-content-center error-font my-3">
								{this.state.error}
							</div>
						</div>
					)}
					<form className="w-100" onSubmit={this.signIn}>
						<div className="px-5 form-group">
							<label
								className="text-grey form-font font-10"
								htmlFor="InputEmail"
							>
								EMAIL
							</label>
							<input
								onChange={this.handleChange.bind(this, 'email')}
								type="email"
								name="email"
								className="form-control"
								id="InputEmail"
								aria-describedby="emailHelp"
								placeholder="Enter Email"
							/>
						</div>
						<div className="mt-3 px-5 form-group mb-2">
							<PasswordInput
								label={
									<label
										className="text-grey form-font font-10"
										htmlFor="InputPassword"
									>
										PASSWORD
									</label>
								}
								onChange={this.handleChange.bind(this, 'password')}
								name="password"
								className="form-control"
								placeholder="Enter Password"
								value={this.state.password}
							/>
						</div>
						<div
							onClick={this.props.doToggleForgotPass}
							className="link-color d-flex justify-content-end mt-2 px-5 font-13 hand"
						>
							Forgot Password?
						</div>
						<div className="d-flex justify-content-between mt-4 px-5">
							<button
								type="submit"
								className="btn btn-primary btn-color btn-lg btn-block font-15"
							>
								{showLoading ? (
									<img src={loadIcon} alt="Loading..." height="23px" />
								) : (
									<span>SIGN IN</span>
								)}
							</button>
						</div>
					</form>
					<div className="mt-4 px-5 d-flex justify-content-center font-13">
						Don&apos;t have an account yet?
						<span
							className="link-color ml-2 font-weight-bold hand"
							onClick={this.moveToSignUp}
						>
							Sign Up
						</span>
					</div>
				</div>

				{this.props.showForgotPassModal && (
					<ForgotPass
						toggle={this.props.doToggleForgotPass}
						forgotPassword={this.props.forgotPassword}
						forgotPasswordMessage={this.props.forgotPasswordMessage}
					/>
				)}
			</div>
		);
	}
}

SignIn.propTypes = {
	location: PropTypes.object.isRequired,
	auth: PropTypes.object.isRequired,
	error: PropTypes.string,
	loginUser: PropTypes.func.isRequired,
	showForgotPassModal: PropTypes.bool.isRequired,
	doToggleForgotPass: PropTypes.func.isRequired,
	forgotPassword: PropTypes.func.isRequired,
	forgotPasswordMessage: PropTypes.string,
	setTrack: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
	signIn: makeSelectSignIn(),
	forgotPasswordMessage: makeSelectForgotPassword(),
	location: makeSelectLocation(),
	error: makeSelectError(),
	auth: makeSelectAuth(),
	showForgotPassModal: makeSelectForgotPassToggle(),
	isMobile: makeSelectIsMobile(),
});

function mapDispatchToProps(dispatch) {
	return {
		dispatch,
		loginUser: (data) => dispatch(loginUser(data)),
		doToggleForgotPass: () => dispatch(toggleForgotPass()),
		forgotPassword: (data) => dispatch(forgotPassword(data)),
		setTrack: (track) => dispatch(setSignupTrack(track)),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(SignIn);
